import apiClient from '../api.js';
import { conToken } from '../autorizacion.js';

// Capa de servicios de las reglas de cobro (HU-05): el costo de un ítem (sin
// gestión), asociado en cada gestión a grupos de carreras de uno o varios planes.
// Una misma regla sirve en varias gestiones.
// Backend: `UNICEN/modules/economico`. Contrato completo:
// UNICEN/modules/economico/docs/reglas-cobro.openapi.yaml
//
// Token obligatorio, `{ data }`, errores `{ mensaje, campo?, rechazadas? }` (leer con `errorDeApi`).
// Montos y porcentajes viajan como texto ('500.00').
//
//   GET    /economico/reglas-cobro/planes/:id_plan/gestiones/:id_gestion/matriz?buscar&pagina&limite&por_evento
//          -> { plan, gestion, grupos, items: [{ id_item, nombre, celdas: [{ id_grupo, cobra, regla }] }], total }
//   GET    /economico/reglas-cobro?id_gestion&id_item                -> { reglas }  (del ítem, de cualquier gestión)
//   GET    /economico/reglas-cobro/grupos-candidatos?id_gestion&id_item&id_tramo? -> { planes: [{ plan, grupos }] }
//          (con id_tramo: la regla que cada grupo tiene en ese tramo de un evento; sin él, la base)
//   GET    /economico/reglas-cobro/planes/:id_plan/gestiones/:id_gestion/eventos -> { reglas }  (precios por tramo de eventos de cobro)
//   POST   /economico/reglas-cobro { id_gestion, id_item, id_tramo?, modo, monto?, id_tipo_moneda?, porcentaje?,
//                                    precio_extranjeros?, id_tipo_moneda_extranjeros?, grupos }
//   PUT    /economico/reglas-cobro/:id_regla { id_gestion, ...valor }  -> { ..., clonada, id_regla_anterior }
//            (regla compartida con otra gestión o ya usada: crea una nueva para los grupos de esa gestión)
//   DELETE /economico/reglas-cobro/:id_regla?id_gestion              (quita la regla de la gestión)
//   PUT    /economico/reglas-cobro/:id_regla/grupos { id_gestion, grupos }
//   POST   /economico/reglas-cobro/:id_regla/grupos/:id_grupo { id_gestion }  (asignar; mueve si tenía otra)
//   DELETE /economico/reglas-cobro/:id_regla/grupos/:id_grupo?id_gestion
//   POST   /economico/reglas-cobro/copias/vista-previa | /copias { id_gestion_origen, id_gestion_destino, id_plan? }
//   GET    /economico/reglas-cobro/historial?id_gestion&id_plan&pagina&limite
//   Catálogo de reglas (sin gestión ni grupos; los grupos se asocian desde TARIFAS):
//   GET    /economico/reglas-cobro/catalogo?id_item&id_tramo&base&uso&pagina&limite -> { reglas, total }
//   POST   /economico/reglas-cobro/catalogo { id_item, id_tramo?, modo, monto?, ... }
//   PUT    /economico/reglas-cobro/catalogo/:id_regla { ...valor }  -> { ..., clonada, id_regla_anterior }
//   DELETE /economico/reglas-cobro/catalogo/:id_regla                (solo sin grupos ni deudas)
//   GET    /economico/reglas-cobro/catalogo/:id_regla/historial?pagina&limite -> { cambios, total }

const resource = '/economico/reglas-cobro';

/** Quita los vacíos: `?id_plan=` no es lo mismo que no mandarlo. */
function sinVacios(params) {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

const useReglasCobroApi = {
  // porEvento: una fila por ítem y tramo de evento (las reglas que usa el motor).
  matriz(idPlan, idGestion, { buscar, pagina, limite, porEvento } = {}) {
    return apiClient.get(`${resource}/planes/${idPlan}/gestiones/${idGestion}/matriz`, conToken({
      params: sinVacios({ buscar, pagina, limite, por_evento: porEvento ? 'true' : undefined }),
    }));
  },

  reglasDeItem(idGestion, idItem) {
    return apiClient.get(resource, conToken({ params: { id_gestion: idGestion, id_item: idItem } }));
  },

  gruposCandidatos(idGestion, idItem, idTramo = null) {
    return apiClient.get(`${resource}/grupos-candidatos`, conToken({ params: sinVacios({ id_gestion: idGestion, id_item: idItem, id_tramo: idTramo }) }));
  },

  /** Los precios por tramo de los eventos de cobro (reglas con tramo) del plan en la gestión. */
  reglasDeEventos(idPlan, idGestion) {
    return apiClient.get(`${resource}/planes/${idPlan}/gestiones/${idGestion}/eventos`, conToken());
  },

  crear(regla) {
    return apiClient.post(resource, sinVacios(regla), conToken());
  },

  /**
   * Cambiar el valor desde una gestión. Si la regla se comparte o ya se usó, el backend crea una nueva (`clonada`).
   * Con `idGrupo` el cambio es solo de ese grupo: los demás que comparten la regla conservan el valor anterior.
   */
  editar(idRegla, idGestion, valor, idGrupo = null) {
    return apiClient.put(`${resource}/${idRegla}`, sinVacios({ id_gestion: idGestion, id_grupo: idGrupo, ...valor }), conToken());
  },

  /** Quitar la regla de la gestión (sus grupos de esa gestión quedan sin precio). */
  eliminar(idRegla, idGestion) {
    return apiClient.delete(`${resource}/${idRegla}`, conToken({ params: { id_gestion: idGestion } }));
  },

  cambiarGrupos(idRegla, idGestion, grupos) {
    return apiClient.put(`${resource}/${idRegla}/grupos`, { id_gestion: idGestion, grupos }, conToken());
  },

  asignarGrupo(idRegla, idGestion, idGrupo) {
    return apiClient.post(`${resource}/${idRegla}/grupos/${idGrupo}`, { id_gestion: idGestion }, conToken());
  },

  quitarGrupo(idRegla, idGestion, idGrupo) {
    return apiClient.delete(`${resource}/${idRegla}/grupos/${idGrupo}`, conToken({ params: { id_gestion: idGestion } }));
  },

  vistaPreviaCopia(copia) {
    return apiClient.post(`${resource}/copias/vista-previa`, sinVacios(copia), conToken());
  },

  copiar(copia) {
    return apiClient.post(`${resource}/copias`, sinVacios(copia), conToken());
  },

  /** Las reglas del catálogo. `uso`: TODAS | ASOCIADAS | SIN_ASOCIAR; `base`: solo las que se cobran siempre. */
  catalogo({
    idItem, idTramo, base, uso, pagina, limite,
  } = {}) {
    return apiClient.get(`${resource}/catalogo`, conToken({
      params: sinVacios({
        id_item: idItem, id_tramo: idTramo, base: base ? 'true' : undefined, uso, pagina, limite,
      }),
    }));
  },

  crearCatalogo(regla) {
    return apiClient.post(`${resource}/catalogo`, sinVacios(regla), conToken());
  },

  /** Cambiar el valor en todas las gestiones. Si ya generó deudas, el backend crea una regla nueva (`clonada`). */
  editarCatalogo(idRegla, valor) {
    return apiClient.put(`${resource}/catalogo/${idRegla}`, sinVacios(valor), conToken());
  },

  eliminarCatalogo(idRegla) {
    return apiClient.delete(`${resource}/catalogo/${idRegla}`, conToken());
  },

  historialDeRegla(idRegla, { pagina, limite } = {}) {
    return apiClient.get(`${resource}/catalogo/${idRegla}/historial`, conToken({ params: sinVacios({ pagina, limite }) }));
  },

  historial({ idGestion, idPlan, pagina, limite } = {}) {
    return apiClient.get(`${resource}/historial`, conToken({ params: sinVacios({ id_gestion: idGestion, id_plan: idPlan, pagina, limite }) }));
  },
};

export default useReglasCobroApi;
